"use client";
import React, { useEffect, useRef, useState } from "react";
import "./ContactForm.scss";
import { AiFillCloseCircle } from "react-icons/ai";

const MAX_CONTACT_ATTACHMENTS = 2;

const WORK_CODE_OPTIONS = [
  {
    label: "AlfaMall Damage/Faulty Product Inquiry",
    value: "alfa_mall_damage_or_faulty_product_received",
  },
  {
    label: "AlfaMall Product Not Received Inquiry",
    value: "alfa_mall_product_not_received",
  },
  {
    label: "Product Not Booked but Amount Debited Inquiry",
    value: "product_not_booked_but_amount_debited",
  },
  {
    label: "SBS Request Not Processed Inquiry",
    value: "sbs_request_not_processed_through_alfamall",
  },
  {
    label: "Others",
    value: "others",
  },
];

const getOrderStatusSummary = (data) => {
  if (!data || typeof data !== "object") {
    return null;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    const nested = getOrderStatusSummary(data.data);
    if (nested) {
      return nested;
    }
  }
  // storefront/customer_order_status: { "status": "In Progress" }
  const rootKeys = Object.keys(data);
  if (
    rootKeys.length === 1 &&
    rootKeys[0] === "status" &&
    typeof data.status === "string" &&
    data.status.trim()
  ) {
    return data.status.trim();
  }

  const parts = [];
  if (typeof data.status === "string" && data.status.trim()) {
    parts.push(data.status.trim());
  }
  if (data.financial_status) {
    parts.push(`Payment: ${data.financial_status}`);
  }
  if (data.fulfillment_status) {
    parts.push(`Fulfillment: ${data.fulfillment_status}`);
  }
  if (data.order_status) {
    parts.push(String(data.order_status));
  }
  if (data.state != null && String(data.state).trim()) {
    parts.push(String(data.state).trim());
  }
  if (data.order_state != null && String(data.order_state).trim()) {
    parts.push(String(data.order_state).trim());
  }
  if (typeof data.message === "string" && data.message.trim()) {
    parts.push(data.message.trim());
  }
  if (parts.length) {
    return parts.join(" · ");
  }
  if (data.delivery_date) {
    return `Est. shipment: ${data.delivery_date}`;
  }
  if (data.name) {
    return `Order ${data.name} — details retrieved`;
  }
  return "Order found";
};

const normalizeOrdersFromApiResponse = (data) => {
  if (!data || typeof data !== "object") {
    return [];
  }
  // customer_order_status: { "status": [ { name, status }, ... ] }
  const statusAsRows =
    Array.isArray(data.status) &&
    data.status.length > 0 &&
    typeof data.status[0] === "object" &&
    data.status[0] !== null;
  const nestedStatusRows =
    data.data &&
    typeof data.data === "object" &&
    Array.isArray(data.data.status) &&
    data.data.status.length > 0 &&
    typeof data.data.status[0] === "object" &&
    data.data.status[0] !== null;

  const raw =
    (Array.isArray(data.orders) && data.orders) ||
    (statusAsRows ? data.status : null) ||
    (nestedStatusRows ? data.data.status : null) ||
    (data.data &&
      typeof data.data === "object" &&
      Array.isArray(data.data.orders) &&
      data.data.orders) ||
    null;
  if (!raw || !raw.length) {
    return [];
  }
  return raw
    .map((item, index) => ({
      name: item?.name != null ? String(item.name).trim() : "",
      status: item?.status != null ? String(item.status).trim() : "",
      courierName: item?.courier_name != null ? String(item.courier_name).trim() : "",
      trackingNumber: item?.tracking_number != null ? String(item.tracking_number).trim() : "",
      key: `${item?.name ?? ""}-${item?.status ?? ""}-${index}`,
    }))
    .filter((row) => row.name || row.status);
};

const orderStatusChipClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (/dispatch|shipped|delivered|complete|approved|paid/.test(s)) {
    return "contact-form__order-chip contact-form__order-chip--success";
  }
  if (/progress|pending|process|hold|wait/.test(s)) {
    return "contact-form__order-chip contact-form__order-chip--pending";
  }
  if (/cancel|fail|reject|error/.test(s)) {
    return "contact-form__order-chip contact-form__order-chip--error";
  }
  return "contact-form__order-chip contact-form__order-chip--neutral";
};

const formatOrderStatusApiError = (errBody) => {
  if (!errBody || typeof errBody !== "object") {
    return null;
  }
  if (errBody.detail != null) {
    return typeof errBody.detail === "string"
      ? errBody.detail
      : JSON.stringify(errBody.detail);
  }
  const parts = [];
  for (const val of Object.values(errBody)) {
    if (Array.isArray(val)) {
      val.forEach((item) => {
        if (typeof item === "string") {
          parts.push(item);
        } else if (item != null) {
          parts.push(String(item));
        }
      });
    } else if (typeof val === "string") {
      parts.push(val);
    }
  }
  return parts.length ? parts.join(" ") : null;
};

const ContactForm = ({ onClose, backgroundColor = "#D62314" }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNo: "",
    inquiryType: "",
    details: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    mobileNo: "",
    inquiryType: "",
    details: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    mobileNo: false,
    inquiryType: false,
    details: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [apiResponse, setApiResponse] = useState("");
  const [attachmentItems, setAttachmentItems] = useState([]);
  const attachmentFileInputRef = useRef(null);
  const attachmentItemsRef = useRef([]);
  attachmentItemsRef.current = attachmentItems;

  const [orderNumber, setOrderNumber] = useState("");
  const [orderLookupLoading, setOrderLookupLoading] = useState(false);
  const [orderLookupResult, setOrderLookupResult] = useState("");
  const [orderLookupOrders, setOrderLookupOrders] = useState([]);
  const [orderLookupError, setOrderLookupError] = useState("");
  const [selectedOption, setSelectedOption] = useState(null); // null, 'checkOrder', 'submitInquiry'

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...errors };

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
      valid = false;
    } else {
      newErrors.name = "";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
      valid = false;
    } else {
      newErrors.email = "";
    }

    // Mobile number validation
    if (!formData.mobileNo.trim()) {
      newErrors.mobileNo = "Mobile number is required";
      valid = false;
    } else if (!formData.mobileNo.startsWith("03")) {
      newErrors.mobileNo = "Mobile number must start with 03";
      valid = false;
    } else if (!/^03\d{9}$/.test(formData.mobileNo)) {
      newErrors.mobileNo = "Please enter a valid phone number (11 digits)";
      valid = false;
    } else {
      newErrors.mobileNo = "";
    }

    // Details validation
    if (!formData.inquiryType.trim()) {
      newErrors.inquiryType = "Select type is required";
      valid = false;
    } else {
      newErrors.inquiryType = "";
    }

    // Details validation
    if (!formData.details.trim()) {
      newErrors.details = "Message is required";
      valid = false;
    } else if (formData.details.trim().length < 10) {
      newErrors.details = "Message must be at least 10 characters";
      valid = false;
    } else {
      newErrors.details = "";
    }

    setErrors(newErrors);
    return valid;
  };

  useEffect(() => {
    return () => {
      attachmentItemsRef.current.forEach((item) => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobileNo") {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue.length <= 11) {
        setFormData({
          ...formData,
          [name]: numericValue,
        });
      }
    } else if (name === "name") {
      // Allow only letters and spaces, no numbers or special characters
      const alphabeticValue = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData({
        ...formData,
        [name]: alphabeticValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleOrderNumberChange = (e) => {
    setOrderNumber(e.target.value);
    setOrderLookupResult("");
    setOrderLookupOrders([]);
    setOrderLookupError("");
  };

  const handleOrderLookup = async () => {
    const id = orderNumber.trim();
    if (!id) {
      setOrderLookupError("Please enter an order number.");
      setOrderLookupResult("");
      return;
    }

    setOrderLookupLoading(true);
    setOrderLookupError("");
    setOrderLookupResult("");
    setOrderLookupOrders([]);

    try {
      const url = `${process.env.NEXT_PUBLIC_BACKEND}/storefront/customer_order_status`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: id }),
      });

      if (!response.ok) {
        let message = "Could not fetch order status.";
        try {
          const errBody = await response.json();
          const formatted = formatOrderStatusApiError(errBody);
          if (formatted) {
            message = formatted;
          }
        } catch {
          if (response.status === 404) {
            message = "Order not found.";
          } else if (response.status === 400) {
            message = "Invalid order number. Please check and try again.";
          }
        }
        setOrderLookupError(message);
        return;
      }

      const data = await response.json();
      const ordersRows = normalizeOrdersFromApiResponse(data);
      if (ordersRows.length > 0) {
        setOrderLookupOrders(ordersRows);
        setOrderLookupResult("");
      } else {
        setOrderLookupOrders([]);
        const summary = getOrderStatusSummary(data);
        setOrderLookupResult(summary || "Order details loaded.");
      }
    } catch (err) {
      console.error("Order lookup failed:", err);
      setOrderLookupError("Something went wrong. Please try again.");
    } finally {
      setOrderLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allTouched = Object.keys(touched).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (validateForm()) {
      // setIsSubmitting(true);
      // setSubmitStatus(null);
      setApiResponse("");

      try {
        const payload = new FormData();
        payload.append("name", formData.name);
        payload.append("email", formData.email);
        payload.append("phone", formData.mobileNo);
        payload.append("inquiry_type", formData.inquiryType);
        payload.append("details", formData.details);

        attachmentItems.forEach(({ file }) => {
          payload.append("attachments", file);
        });

        if (process.env.NODE_ENV === "development") {
          const logEntries = [];
          payload.forEach((value, key) => {
            logEntries.push(
              value instanceof File
                ? { key, file: value.name, type: value.type, size: value.size }
                : { key, value: String(value) }
            );
          });
}

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND}/storefront/customer_inquiry`,
          {
            method: "POST",
            body: payload,
          }
        );

        if (response.ok) {
          const data = await response.json();
setSubmitStatus("success");
          setApiResponse(data.detail || "Thank you! We will contact you soon.");
          setTimeout(() => {
            onClose();
          }, 3000);
        } else {
          console.error("API error:", response.status);
          setSubmitStatus("error");
          setApiResponse("Failed to submit form. Please try again.");
        }
      } catch (error) {
        console.error("Request failed:", error);
        setSubmitStatus("error");
        setApiResponse("An error occurred. Please try again later.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleAttachmentInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!files.length) {
      return;
    }

    setAttachmentItems((prev) => {
      const next = [...prev];
      let remaining = MAX_CONTACT_ATTACHMENTS - next.length;
      for (const file of files) {
        if (remaining <= 0) {
          break;
        }
        if (!file.type.startsWith("image/")) {
          continue;
        }
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          file,
          preview: URL.createObjectURL(file),
        });
        remaining -= 1;
      }
      return next;
    });
  };

  const handleAddPhotoClick = () => {
    if (attachmentItems.length >= MAX_CONTACT_ATTACHMENTS) {
      return;
    }
    attachmentFileInputRef.current?.click();
  };

  const handleAttachmentRemove = (id) => {
    setAttachmentItems((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (item?.preview) {
        URL.revokeObjectURL(item.preview);
      }
      return prev.filter((entry) => entry.id !== id);
    });
  };

  const handleNumericKeyDown = (e) => {
    if (
      [8, 46, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 67 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 88 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 86 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }

    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  const renderContent = () => {
    if (submitStatus === "success") {
      return (
        <div className="contact-form__success-container">
          <div className="contact-form__success-icon">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="40" cy="40" r="40" fill={backgroundColor} />
              <path
                d="M35 50L28 43M35 50L52 33"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="contact-form__success-message">{apiResponse}</p>
        </div>
      );
    }

    // Show option selection screen
    if (selectedOption === null) {
      return (
        <div className="contact-form__options">
          <h2 className="contact-form__title">How can we help you?</h2>
          <p className="contact-form__subtitle">Please select an option below</p>
          
          <div className="contact-form__option-buttons">
            <button
              type="button"
              className="contact-form__option-btn"
              onClick={() => setSelectedOption('checkOrder')}
              style={{ '--btn-color': backgroundColor }}
            >
              <div className="contact-form__option-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="contact-form__option-title">Check Order Status</span>
              <span className="contact-form__option-desc">Track your order and view delivery status</span>
            </button>

            <button
              type="button"
              className="contact-form__option-btn"
              onClick={() => setSelectedOption('submitInquiry')}
              style={{ '--btn-color': backgroundColor }}
            >
              <div className="contact-form__option-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="contact-form__option-title">Submit Inquiry</span>
              <span className="contact-form__option-desc">Send us a message or report an issue</span>
            </button>
          </div>
        </div>
      );
    }

    // Show Check Order Status section
    if (selectedOption === 'checkOrder') {
      return (
        <div className="contact-form__content">
          <button
            type="button"
            className="contact-form__back-btn"
            onClick={() => {
              setSelectedOption(null);
              setOrderNumber("");
              setOrderLookupResult("");
              setOrderLookupOrders([]);
              setOrderLookupError("");
            }}
          >
            ← Back
          </button>
          
          <h2 className="contact-form__title">Check Order Status</h2>

          <div className="contact-form__field">
            <label htmlFor="orderNumberLookup">Order number</label>
            <div className="contact-form__order-row">
              <div className="contact-form__order-input-wrap">
                <input
                  type="text"
                  id="orderNumberLookup"
                  name="orderNumberLookup"
                  value={orderNumber}
                  onChange={handleOrderNumberChange}
                  placeholder="Enter order number"
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (!orderLookupLoading) {
                        handleOrderLookup();
                      }
                    }
                  }}
                />
              </div>
              <button
                type="button"
                className="contact-form__order-check-btn"
                style={{ backgroundColor }}
                onClick={handleOrderLookup}
                disabled={orderLookupLoading}
              >
                {orderLookupLoading ? "Checking…" : "Check status"}
              </button>
            </div>
            {orderLookupOrders.length > 0 && (
              <div className="contact-form__order-status-wrap" role="status">
                <span className="contact-form__order-status-label">Order Status</span>
                <div className="contact-form__order-cards">
                  {orderLookupOrders.map((row) => (
                    <div key={row.key} className="contact-form__order-card">
                      <div className="contact-form__order-card-header">
                        <span className="contact-form__order-card-label">Status</span>
                        <span className={orderStatusChipClass(row.status)}>
                          {row.status || "—"}
                        </span>
                      </div>
                      <div className="contact-form__order-card-body">
                        <div className="contact-form__order-info-row">
                          <span className="contact-form__order-info-label">Order no.</span>
                          <span className="contact-form__order-info-value">
                            {row.name || "—"}
                          </span>
                        </div>
                        {row.courierName && (
                          <div className="contact-form__order-info-row">
                            <span className="contact-form__order-info-label">Courier</span>
                            <span className="contact-form__order-info-value">
                              {row.courierName}
                            </span>
                          </div>
                        )}
                        {row.trackingNumber && (
                          <div className="contact-form__order-info-row">
                            <span className="contact-form__order-info-label">Tracking no.</span>
                            <span className="contact-form__order-info-value">
                              {row.trackingNumber}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {orderLookupResult && orderLookupOrders.length === 0 && (
              <div className="contact-form__order-status-wrap" role="status">
                <span className="contact-form__order-status-label">Order status</span>
                <div className="contact-form__order-chips">
                  {orderLookupResult.split(" · ").map((part, chipIndex) => (
                    <span
                      key={chipIndex}
                      className="contact-form__order-chip contact-form__order-chip--success"
                    >
                      {part.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {orderLookupError && (
              <div className="contact-form__order-status-wrap" role="alert">
                <div className="contact-form__order-chips">
                  <span className="contact-form__order-chip contact-form__order-chip--error">
                    {orderLookupError}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Show Submit Inquiry form
    return (
      <form onSubmit={handleSubmit} className="contact-form__content">
        <button
          type="button"
          className="contact-form__back-btn"
          onClick={() => setSelectedOption(null)}
        >
          ← Back
        </button>
        
        <h2 className="contact-form__title">Submit Inquiry</h2>

        <div className="contact-form__field">
          <label htmlFor="name">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your name"
            className={errors.name && touched.name ? "error" : ""}
          />
          {errors.name && touched.name && (
            <p className="contact-form__error">{errors.name}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="email">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your email"
            className={errors.email && touched.email ? "error" : ""}
          />
          {errors.email && touched.email && (
            <p className="contact-form__error">{errors.email}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="mobileNo">
            Mobile Number <span className="required">*</span>
          </label>
          <input
            type="text"
            id="mobileNo"
            name="mobileNo"
            value={formData.mobileNo}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleNumericKeyDown}
            placeholder="03XXXXXXXXX"
            maxLength={11}
            inputMode="numeric"
            className={errors.mobileNo && touched.mobileNo ? "error" : ""}
          />
          {errors.mobileNo && touched.mobileNo && (
            <p className="contact-form__error">{errors.mobileNo}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="inquiryType">
            Select Type <span className="required">*</span>
          </label>
          <select
            id="inquiryType"
            name="inquiryType"
            value={formData.inquiryType}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.inquiryType && touched.inquiryType ? "error" : ""}
          >
            <option value="" disabled>
              Select issue type
            </option>
            {WORK_CODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.inquiryType && touched.inquiryType && (
            <p className="contact-form__error">{errors.inquiryType}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="details">
            Message <span className="required">*</span>
          </label>
          <textarea
            id="details"
            name="details"
            value={formData.details}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your message (minimum 10 characters)"
            rows="4"
            className={errors.details && touched.details ? "error" : ""}
          />
          {errors.details && touched.details && (
            <p className="contact-form__error">{errors.details}</p>
          )}
        </div>

        <div className="contact-form__field contact-form__field--photos">
          <div className="contact-form__attachment-toolbar">
            <input
              ref={attachmentFileInputRef}
              key={`attachment-input-${attachmentItems.length}`}
              type="file"
              multiple={attachmentItems.length === 0}
              accept="image/*"
              className="contact-form__attachment-input-hidden"
              onChange={handleAttachmentInputChange}
              tabIndex={-1}
              aria-describedby="contact-attachment-hint"
            />
            <button
              type="button"
              className="contact-form__add-photo-btn"
              onClick={handleAddPhotoClick}
              disabled={attachmentItems.length >= MAX_CONTACT_ATTACHMENTS}
              aria-label="Add photos"
            >
              Add Photo
            </button>
            <span id="contact-attachment-hint" className="contact-form__attachment-hint">
              Add up to {MAX_CONTACT_ATTACHMENTS} photos.
              {attachmentItems.length === 0 && " Up to two images per selection."}
              {attachmentItems.length === 1 && " Select one image for the second slot."}
            </span>
          </div>
          <div className="contact-form__attachment-previews" aria-live="polite">
            {attachmentItems.map((item, index) => (
              <div key={item.id} className="contact-form__attachment-slot">
                <div className="contact-form__attachment-thumb-wrap">
                  <img
                    src={item.preview}
                    alt={`Uploaded photo ${index + 1}`}
                    className="contact-form__attachment-thumb"
                  />
                  <button
                    type="button"
                    className="contact-form__attachment-remove"
                    onClick={() => handleAttachmentRemove(item.id)}
                    aria-label={`Remove photo ${index + 1}`}
                  >
                    <span className="contact-form__attachment-remove-x" aria-hidden="true">
                      ×
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="contact-form__submit-btn"
          disabled={isSubmitting}
          style={{ backgroundColor }}
        >
          {isSubmitting ? "Submitting..." : "Send Message"}
        </button>

        {submitStatus === "error" && (
          <p className="contact-form__status contact-form__status--error">
            {apiResponse}
          </p>
        )}
      </form>
    );
  };

  return (
    <div className="contact-form" style={{ "--background-color": backgroundColor }}>
      <div className="contact-form__header">
        <h3>How may I help you?</h3>
        <button
          className="contact-form__close-btn"
          onClick={onClose}
          aria-label="Close form"
        >
          <AiFillCloseCircle size={24} />
        </button>
      </div>

      <div className="contact-form__card">{renderContent()}</div>
    </div>
  );
};

export default ContactForm;
